"use client"

import { ExclamationTriangleIcon } from "@heroicons/react/16/solid"
import {
  ArrowRightIcon,
  ChevronRightIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/20/solid"
import ActivityDockMobile from "@src/components/ActivityDockMobile"
import Button from "@src/components/Button"
import ConfidentialToggleMobile from "@src/components/ConfidentialToggleMobile"
import AssetComboIcon from "@src/components/DefuseSDK/components/Asset/AssetComboIcon"
import { FormattedCurrency } from "@src/components/DefuseSDK/features/account/components/shared/FormattedCurrency"
import type { Holding } from "@src/components/DefuseSDK/features/account/types/sharedTypes"
import { computeTotalUsdValue } from "@src/components/DefuseSDK/features/account/utils/holdingsUtils"
import { useNearStakingPosition } from "@src/components/DefuseSDK/features/nearStaking"
import { NEAR_STAKING_APR_BPS } from "@src/components/DefuseSDK/features/nearStaking/config"
import { QrPayScannerButton } from "@src/components/DefuseSDK/features/qrPay/QrPayScannerButton"
import { useHideBalances } from "@src/components/DefuseSDK/hooks/useHideBalances"
import {
  getDefuseAssetId,
  getTokenAid,
} from "@src/components/DefuseSDK/utils/token"
import Explore from "@src/components/Explore"
import SystemStatusMobile from "@src/components/SystemStatusMobile"
import TokensNetworksPromo from "@src/components/TokensNetworksPromo"
import UserMenuMobile from "@src/components/UserMenuMobile"
import { VipSupportCard } from "@src/components/VipSupportCard"
import { buildAccountSummary } from "@src/features/accountSummary/accountSummary"
import { computeEarnHomeMetrics } from "@src/features/earn/lib/computeEarnHomeMetrics"
import { formatApy } from "@src/features/earn/lib/formatApy"
import { useCuratedEarnVaults } from "@src/features/earn/lib/useCuratedEarnVaults"
import { useEarnVaultBalance } from "@src/features/earn/lib/useEarnVaultBalance"
import {
  TRADE_XYZ_DEX,
  useHyperliquidAccount,
  useHyperliquidMarkets,
  useHyperliquidTradingBalance,
} from "@src/features/trade/hyperliquid"
import { aggregatePerpsAccount } from "@src/features/trade/hyperliquid/perpsAccount"
import { fmtCompactUsd } from "@src/features/trade/hyperliquid/utils/format"
import { useTradingSignerAddress } from "@src/features/tradingSigner/useTradingSignerAddress"
import { useAccountData } from "@src/hooks/useAccountData"
import {
  ArrowThickDownIcon,
  SendIcon,
  TriangleDownIcon,
  TriangleUpIcon,
} from "@src/icons"

import { useConfidentialMode } from "@src/providers/ConfidentialModeProvider"
import { trackAssetAction } from "@src/utils/trackAssetAction"
import clsx from "clsx"
import Link from "next/link"
import type { ReactNode } from "react"

export default function HomePage() {
  const { confidentialMode } = useConfidentialMode()
  const { hidden: hideBalances, toggle: toggleHideBalances } = useHideBalances()
  const {
    holdings,
    privateHoldings,
    isHoldingsPending,
    isPrivateBalancePending,
    hasPrivateBalanceLoadError,
    hasHoldingsLoadError,
    usdPrices,
    userId,
  } = useAccountData()

  const totalMainValueUsd = computeTotalUsdValue(holdings ?? [])
  const totalConfidentialValueUsd = computeTotalUsdValue(privateHoldings)
  const cryptoValueUsd = totalMainValueUsd + totalConfidentialValueUsd

  const {
    address: tradingAddress,
    isLoading: tradingAddressLoading,
    isError: tradingAddressError,
  } = useTradingSignerAddress()
  const { data: tradingBalance, isLoading: tradingBalanceLoading } =
    useHyperliquidTradingBalance(tradingAddress)
  const { data: tradeXyzAccount, isLoading: tradeXyzAccountLoading } =
    useHyperliquidAccount(tradingAddress, TRADE_XYZ_DEX)
  const { data: perpsMarkets, isPending: perpsMarketsLoading } =
    useHyperliquidMarkets()
  const perpsAccount = tradingBalance?.ok
    ? aggregatePerpsAccount({
        tradingBalance: tradingBalance.data,
        additionalAccountStates: tradeXyzAccount?.ok
          ? [tradeXyzAccount.data]
          : [],
      })
    : null
  const perpsValueUsd = perpsAccount?.equity ?? 0
  const perpsPositionCount = perpsAccount?.positions.length ?? 0
  const perpsUnrealizedPnl = perpsAccount?.unrealizedPnl ?? 0
  const perpsLoading =
    tradingAddressLoading || tradingBalanceLoading || tradeXyzAccountLoading
  const perpsLoadError =
    tradingAddressError ||
    tradingBalance?.ok === false ||
    tradeXyzAccount?.ok === false

  const earnVaultBalance = useEarnVaultBalance()
  const { vaults: earnVaults, summaryQuery: earnVaultSummaryQuery } =
    useCuratedEarnVaults()
  const nearStaking = useNearStakingPosition(userId)
  const earnValueUsd = earnVaultBalance.valueUsd + (nearStaking.stakedUsd ?? 0)
  const earnLoading = earnVaultBalance.loading || nearStaking.stakedUsdLoading
  const earnLoadError =
    earnVaultBalance.loadError || nearStaking.stakedUsdLoadError
  const vaultAprByAssetId = Object.fromEntries(
    earnVaults.map((vault) => [vault.vaultToken.defuseAssetId, vault.apr])
  )
  const { positionCount: earnPositionCount, blendedApy: earnBlendedApy } =
    computeEarnHomeMetrics({
      vaultHoldings: earnVaultBalance.holdings,
      vaultPositionCount: earnVaultBalance.positionCount,
      vaultAprByAssetId,
      stakedNear: nearStaking.stakedNear,
      stakedUsd: nearStaking.stakedUsd,
      nearStakingAprPercent: NEAR_STAKING_APR_BPS / 100,
    })
  const cryptoAssetsById = new Map<
    string,
    { token: Holding["token"]; valueUsd: number }
  >()

  for (const { token, usdValue } of [...(holdings ?? []), ...privateHoldings]) {
    const assetId = getCryptoAssetGroupId(token)
    const existing = cryptoAssetsById.get(assetId)

    if (existing) {
      existing.valueUsd += usdValue ?? 0
    } else {
      cryptoAssetsById.set(assetId, { token, valueUsd: usdValue ?? 0 })
    }
  }

  const cryptoAssets = Array.from(cryptoAssetsById.values()).sort(
    (a, b) => b.valueUsd - a.valueUsd
  )
  const cryptoPreviewAssets = cryptoAssets.slice(0, 4)
  const cryptoMoreCount = Math.max(
    0,
    cryptoAssets.length - cryptoPreviewAssets.length
  )
  const showCryptoSymbolPreview =
    cryptoAssets.length > 0 && cryptoAssets.length < 4
  const showPerpsEmptyTeaser =
    (cryptoValueUsd > 0 || perpsValueUsd > 0) && perpsPositionCount === 0
  const maxPerpsLeverage = perpsMarkets?.ok
    ? getMaxPerpsLeverage(perpsMarkets.data)
    : 0
  const perpsTeaserLoading = showPerpsEmptyTeaser && perpsMarketsLoading
  const showEarnEmptyTeaser =
    cryptoValueUsd > 0 && earnValueUsd <= 0 && earnPositionCount === 0
  const highestEarnYield = getHighestEarnYield(earnVaults)
  const earnTeaserLoading =
    showEarnEmptyTeaser && earnVaultSummaryQuery.isPending

  const accountSummary = buildAccountSummary([
    {
      valueUsd: totalMainValueUsd,
      loading: isHoldingsPending || !usdPrices,
      loadError: hasHoldingsLoadError,
    },
    {
      valueUsd: totalConfidentialValueUsd,
      loading: isPrivateBalancePending || !usdPrices,
      loadError: hasPrivateBalanceLoadError,
    },
    {
      valueUsd: perpsValueUsd,
      loading: perpsLoading,
      loadError: perpsLoadError,
    },
    {
      valueUsd: earnValueUsd,
      loading: earnLoading,
      loadError: earnLoadError,
    },
  ])

  const balanceRows = [
    {
      label: "Crypto",
      description: formatAssetCount(cryptoAssets.length),
      href: "/crypto",
      supportingContent: (
        <div className="flex min-w-0 items-center gap-1.5">
          <div className="-space-x-1.5 flex items-center">
            {cryptoPreviewAssets.map(({ token }) => (
              <AssetComboIcon
                key={getCryptoAssetGroupId(token)}
                icon={token.icon}
                sizeClassName="size-6"
                className="rounded-full outline-2 outline-white group-hover:outline-gray-50 group-focus-visible:outline-gray-50 dark:outline-gray-850 group-focus-visible:dark:outline-gray-800 group-hover:dark:outline-gray-800"
              />
            ))}
          </div>
          {showCryptoSymbolPreview && (
            <span className="truncate font-medium text-gray-500 text-sm/6 dark:text-gray-400">
              {cryptoPreviewAssets.map(({ token }) => token.symbol).join(", ")}
            </span>
          )}
          {cryptoMoreCount > 0 && (
            <span className="font-medium text-gray-500 text-sm/6 dark:text-gray-400">
              +{cryptoMoreCount} more
            </span>
          )}
        </div>
      ),
      valueUsd: cryptoValueUsd,
      loading: isHoldingsPending || isPrivateBalancePending || !usdPrices,
      loadError: hasHoldingsLoadError || hasPrivateBalanceLoadError,
    },
    {
      label: "Perps",
      description: formatPositionCount(perpsPositionCount),
      href: "/perps",
      supportingContent:
        perpsPositionCount > 0 ? (
          <div className="flex items-center gap-1.5">
            <div
              className={clsx(
                "flex items-center gap-0.5 rounded-md pr-2 pl-1",
                {
                  "bg-gray-900/10 text-gray-600 dark:bg-white/10 dark:text-gray-300":
                    hideBalances || perpsUnrealizedPnl === 0,
                  "bg-green-600/15 text-green-800 dark:bg-green-500/15 dark:text-green-500":
                    !hideBalances && perpsUnrealizedPnl > 0,
                  "bg-red-500/15 text-red-800 dark:text-red-400":
                    !hideBalances && perpsUnrealizedPnl < 0,
                }
              )}
            >
              {!hideBalances && perpsUnrealizedPnl > 0 && (
                <TriangleUpIcon className="size-4 shrink-0" />
              )}
              {!hideBalances && perpsUnrealizedPnl < 0 && (
                <TriangleDownIcon className="size-4 shrink-0" />
              )}
              {(hideBalances || perpsUnrealizedPnl === 0) && (
                <div className="flex size-4 shrink-0 items-center justify-center">
                  <div className="size-1.5 rounded-full bg-current" />
                </div>
              )}
              <div
                className={clsx(
                  "font-semibold",
                  hideBalances
                    ? "-tracking-[0.07em] -mt-px pb-px text-base/6"
                    : "text-sm/6 tracking-tight"
                )}
              >
                {hideBalances
                  ? "•••••"
                  : fmtCompactUsd(perpsUnrealizedPnl, { showSigned: false })}
              </div>
            </div>
            <span className="font-medium text-gray-500 text-sm/6 dark:text-gray-400">
              unrealized P&L
            </span>
          </div>
        ) : perpsTeaserLoading ? (
          <BalanceRowSupportingContentLoader />
        ) : showPerpsEmptyTeaser ? (
          <BalanceRowTeaser>
            {maxPerpsLeverage > 0 ? (
              <>
                Trade with up to{" "}
                <span className="font-semibold text-gray-900 dark:text-white">
                  {maxPerpsLeverage}x
                </span>{" "}
                leverage
              </>
            ) : (
              "Go long or short with leverage"
            )}
          </BalanceRowTeaser>
        ) : null,
      valueUsd: perpsValueUsd,
      loading: perpsLoading,
      loadError: perpsLoadError,
    },
    {
      label: "Earn",
      description: formatPositionCount(earnPositionCount),
      href: "/earn",
      supportingContent:
        earnBlendedApy == null ? (
          earnTeaserLoading ? (
            <BalanceRowSupportingContentLoader />
          ) : showEarnEmptyTeaser ? (
            <BalanceRowTeaser>
              Earn up to{" "}
              <span className="font-semibold text-gray-900 dark:text-white">
                {formatApy(highestEarnYield)}
              </span>{" "}
              per year
            </BalanceRowTeaser>
          ) : null
        ) : (
          <div className="flex items-center gap-1.5">
            <div className="flex items-center gap-0.5 rounded-md bg-sky-500/15 pr-2 pl-1 text-sky-700 dark:text-sky-300">
              <div className="flex size-4 shrink-0 items-center justify-center">
                <div className="size-1.5 rounded-full bg-current dark:bg-sky-300" />
              </div>
              <div className="font-semibold text-sm/6 tracking-tight">
                {formatApy(earnBlendedApy)}
              </div>
            </div>
            <span className="font-medium text-gray-500 text-sm/6 dark:text-gray-400">
              blended APY
            </span>
          </div>
        ),
      valueUsd: earnValueUsd,
      loading: earnLoading,
      loadError: earnLoadError,
    },
  ]

  return (
    <>
      <h1 className="sr-only">Home</h1>

      <div className="space-y-10 pt-safe-offset-3 sm:space-y-16 sm:pt-safe-offset-6 xl:pt-0">
        {/* Mobile top bar */}
        <div className="flex items-center justify-between gap-2 xl:hidden">
          <UserMenuMobile />

          <div className="flex items-center justify-end gap-1">
            <SystemStatusMobile />
            <ActivityDockMobile />
            <QrPayScannerButton />
            <ConfidentialToggleMobile />
          </div>
        </div>

        <section className="space-y-10">
          <header className="space-y-6">
            <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="font-medium text-base/6 text-gray-500 dark:text-gray-400">
                    Total balance
                  </h2>

                  <button
                    type="button"
                    onClick={toggleHideBalances}
                    className="relative flex size-6 items-center justify-center rounded-md text-gray-400 hover:text-gray-900 focus-visible:text-gray-900 focus-visible:outline-2 focus-visible:outline-gray-900 focus-visible:outline-offset-2 dark:focus-visible:text-white dark:focus-visible:outline-white dark:hover:text-white"
                  >
                    <span
                      className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 size-10"
                      aria-hidden="true"
                    />
                    {hideBalances ? (
                      <EyeSlashIcon className="size-4 shrink-0" />
                    ) : (
                      <EyeIcon className="size-4 shrink-0" />
                    )}
                    <span className="sr-only">
                      {hideBalances ? "Show balances" : "Hide balances"}
                    </span>
                  </button>
                </div>

                <FormattedCurrency
                  value={accountSummary.totalValueUsd}
                  formatOptions={{ currency: "USD" }}
                  loading={accountSummary.totalValueLoading}
                  className="-tracking-[0.04em] mt-1 font-medium text-[2.5rem]/11 dark:text-white"
                  hideBalances={hideBalances}
                  animated
                />
              </div>

              {accountSummary.isFunded && (
                <div className="flex gap-1">
                  <Button
                    size="lg"
                    className="h-11! flex-1 md:flex-initial"
                    href="/receive"
                    onClick={
                      confidentialMode
                        ? undefined
                        : () => trackAssetAction("deposit", "main")
                    }
                  >
                    <span className="flex items-center justify-center gap-2 pr-2 pl-1">
                      <ArrowThickDownIcon className="size-4.5" />
                      Receive
                    </span>
                  </Button>
                  <Button
                    size="lg"
                    className="h-11! flex-1 md:flex-initial"
                    href={confidentialMode ? "/send/confidential" : "/send"}
                    onClick={
                      confidentialMode
                        ? undefined
                        : () => trackAssetAction("withdraw", "main")
                    }
                  >
                    <span className="flex items-center justify-center gap-2 pr-2 pl-1">
                      <SendIcon className="size-4.5" />
                      Send
                    </span>
                  </Button>
                </div>
              )}
            </div>

            {!accountSummary.isFunded && (
              <div className="flex flex-col items-start gap-10 rounded-2xl border border-transparent bg-gray-800 px-6 pt-5 pb-6 sm:flex-row sm:items-center sm:justify-between sm:px-9 dark:border-white/5 dark:bg-gray-900">
                <div className="flex flex-col items-start gap-3 sm:gap-5">
                  <h2 className="text-pretty font-semibold text-white text-xl/7 tracking-tight">
                    Fund your account to get started
                  </h2>

                  <TokensNetworksPromo className="text-gray-800 dark:text-gray-900" />
                </div>

                <Button
                  size="lg"
                  className="dark h-11!"
                  href="/receive"
                  onClick={
                    confidentialMode
                      ? undefined
                      : () => trackAssetAction("deposit", "main")
                  }
                >
                  <span className="flex items-center justify-center gap-2 pr-2 pl-1">
                    <ArrowThickDownIcon className="size-4.5" />
                    Receive
                  </span>
                </Button>
              </div>
            )}
          </header>

          <div className="rounded-2xl border border-gray-200 bg-gray-50 px-1 pb-1 dark:border-white/5 dark:bg-gray-900">
            <div className="px-5 py-3 sm:px-8">
              <h3 className="font-semibold text-gray-900 text-sm/5 dark:text-white">
                Balances
              </h3>
            </div>

            <ul className="space-y-1 overflow-hidden sm:space-y-0 sm:divide-gray-200 sm:divide-y! sm:rounded-xl sm:border sm:border-gray-200 dark:divide-white/5 sm:dark:border-white/5">
              {balanceRows.map(
                ({
                  label,
                  description,
                  href,
                  supportingContent,
                  valueUsd,
                  loading,
                  loadError,
                }) => (
                  <li
                    key={label}
                    className="rounded-xl border border-gray-200 bg-white focus-within:bg-gray-50 hover:bg-gray-50 sm:rounded-none sm:border-0 dark:border-white/5 dark:bg-gray-850 dark:hover:bg-gray-800 dark:focus-within:bg-gray-800"
                  >
                    <Link
                      href={href}
                      className="group flex flex-col gap-6 px-5 py-4 focus-visible:outline-none sm:grid sm:grid-cols-[0.8fr_1.2fr_1fr] sm:gap-0 sm:px-8 sm:py-5 md:grid-cols-[1.2fr_1.2fr_1fr]"
                    >
                      <div className="flex flex-col">
                        <div className="flex flex-1 items-center justify-between">
                          <h4 className="font-semibold text-base/6 text-gray-900 tracking-tight dark:text-white">
                            {label}
                          </h4>
                          <ArrowRightIcon className="size-5 shrink-0 text-gray-900 sm:hidden dark:text-white" />
                        </div>

                        <div className="sm:hidden">
                          <FormattedCurrency
                            value={valueUsd}
                            formatOptions={{ currency: "USD" }}
                            loading={loading}
                            hideBalances={hideBalances}
                            animated
                            className="font-semibold text-gray-900 text-xl/7 tracking-tight dark:text-white"
                          />
                        </div>

                        {loading ? (
                          <div className="hidden h-5 w-16 items-center sm:flex">
                            <div className="h-3.5 flex-1 animate-pulse rounded bg-gray-150 dark:bg-white/10" />
                          </div>
                        ) : (
                          <p className="hidden font-medium text-gray-500 text-sm/5 sm:block dark:text-gray-400">
                            {description}
                          </p>
                        )}
                      </div>

                      <div className="flex min-h-6 items-center">
                        {loadError ? (
                          <div className="flex items-center gap-1">
                            <ExclamationTriangleIcon className="mt-px size-4 text-yellow-500 dark:text-yellow-300" />
                            <span className="font-semibold text-sm/5 text-yellow-800 dark:text-yellow-300">
                              {valueUsd > 0
                                ? "Failed to update"
                                : "Failed to load"}
                            </span>
                          </div>
                        ) : loading ? (
                          <div className="flex h-6 w-36 items-center">
                            <div className="h-5 flex-1 animate-pulse rounded-md bg-gray-150 dark:bg-white/10" />
                          </div>
                        ) : (
                          supportingContent
                        )}
                      </div>

                      <div className="hidden items-center justify-end gap-8 text-right sm:flex">
                        <span className="font-semibold text-base/6 text-gray-900 tracking-tight dark:text-white">
                          <FormattedCurrency
                            value={valueUsd}
                            formatOptions={{ currency: "USD" }}
                            loading={loading}
                            hideBalances={hideBalances}
                            animated
                          />
                        </span>

                        <ChevronRightIcon className="size-5 shrink-0 text-gray-900 dark:text-white" />
                      </div>
                    </Link>
                  </li>
                )
              )}
            </ul>
          </div>
        </section>

        <VipSupportCard />

        <Explore />
      </div>
    </>
  )
}

function formatPositionCount(count: number) {
  return `${count} ${count === 1 ? "position" : "positions"}`
}

function formatAssetCount(count: number) {
  return `${count} ${count === 1 ? "asset" : "assets"}`
}

function getMaxPerpsLeverage(markets: { maxLeverage: number }[]) {
  return markets.reduce((maxLeverage, market) => {
    return Number.isFinite(market.maxLeverage)
      ? Math.max(maxLeverage, market.maxLeverage)
      : maxLeverage
  }, 0)
}

function getHighestEarnYield(vaults: { apr?: number }[]) {
  return vaults.reduce((highestYield, vault) => {
    return Number.isFinite(vault.apr)
      ? Math.max(highestYield, vault.apr ?? 0)
      : highestYield
  }, NEAR_STAKING_APR_BPS / 100)
}

function getCryptoAssetGroupId(token: Holding["token"]) {
  return getTokenAid(token) ?? getDefuseAssetId(token)
}

function BalanceRowTeaser({ children }: { children: ReactNode }) {
  return (
    <span className="font-medium text-gray-500 text-sm/6 dark:text-gray-400">
      {children}
    </span>
  )
}

function BalanceRowSupportingContentLoader() {
  return (
    <div className="flex h-6 w-36 items-center">
      <div className="h-5 flex-1 animate-pulse rounded-md bg-gray-150 dark:bg-white/10" />
    </div>
  )
}
